/**
 * Date helpers. The API speaks UTC ISO-8601; the UI shows local time, matching
 * ``utilities.formatting.format_datetime`` (which does `.astimezone()` first).
 */

const pad = (value: number) => String(value).padStart(2, "0");

/** `2026-07-30 14:05` in the viewer's own timezone. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `yyyy-MM-dd` for a date input, in local time. */
export function toDateInputValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function todayInputValue(): string {
  return toDateInputValue(new Date());
}

/** Local midnight of `yyyy-MM-dd`, as UTC ISO — an inclusive range start. */
export function startOfDayIso(value: string): string | null {
  const parts = value.split("-").map(Number);
  const [year, month, day] = parts;
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0).toISOString();
}

/** Local end-of-day of `yyyy-MM-dd`, as UTC ISO — an inclusive range end. */
export function endOfDayIso(value: string): string | null {
  const parts = value.split("-").map(Number);
  const [year, month, day] = parts;
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
}

function localDayStart(offsetDays = 0): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + offsetDays, 0, 0, 0, 0);
}

/**
 * Date presets, same windows the desktop pages use: "Last 7 Days" counts back
 * 6 days from today's local midnight so today is the seventh.
 */
export const DATE_PRESETS = [
  "All Time",
  "Today",
  "Last 7 Days",
  "Last 30 Days",
  "Last 90 Days",
  "This Month",
  "Custom Range",
] as const;

export type DatePreset = (typeof DATE_PRESETS)[number];

export function presetToRange(preset: DatePreset): { from: string | null; to: string | null } {
  switch (preset) {
    case "Today":
      return { from: localDayStart().toISOString(), to: null };
    case "Last 7 Days":
      return { from: localDayStart(-6).toISOString(), to: null };
    case "Last 30 Days":
      return { from: localDayStart(-29).toISOString(), to: null };
    case "Last 90 Days":
      return { from: localDayStart(-89).toISOString(), to: null };
    case "This Month": {
      const now = new Date();
      return { from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), to: null };
    }
    default:
      return { from: null, to: null };
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  return `${value.toFixed(1)} ${units[index]}`;
}
